import { ref, watch } from 'vue'
import { defineStore } from 'pinia'

const PAGE_TO_PATH = {
  salidas: '/salidas',
  mapaTerritorios: '/mapa',
  mapaCalor: '/mapa-calor',
  listaTerritorio: '/territorios',
  estadisticas: '/informes',
  rutas: '/rutas',
  tAsignados: '/asignados',
  formTerritorio: '/territorio',
  crearTerritorios: '/territorios-nuevos',
  tarjetaTerritorio: '/tarjeta',
  parametros: '/configuracion',
  downloadTerritorio: '/configuracion',
  userManagement: '/usuarios',
  adminPanel: '/admin',
  gruposInvitados: '/grupos-invitados',
  plantillasMensaje: '/plantillas-mensaje',
  metadataEnlaces: '/metadata-enlaces',
  alojamientos: '/alojamientos',
  estudiosTerritorio: '/estudios',
  transporteTerritorio: '/transporte',
  salidaPublica: '/salida-publica',
  salidaPublicaMayores: '/salida-publica-mayores',
  monitorSalida: '/monitor',
  enlacesSalidas: '/enlaces',
  enlacesInformes: '/enlaces-informes',
  informeConductor: '/informe-conductor',
  alojamientoPublico: '/alojamiento-publico',
  estudiosPublico: '/estudios-publico',
  estudioAsignacionPublico: '/estudio-asignacion',
  registroCongregacion: '/registro-congregacion',
  gestionUsuarios: '/gestion-usuarios',
  estudiosDashboard: '/enlaces-estudios',
  estudiosDashboardPublico: '/estudios-dashboard-publico',
  legal: '/legal',
}

const PATH_TO_PAGE = {
  '/salidas': 'salidas',
  '/mapa': 'mapaTerritorios',
  '/mapa-calor': 'mapaCalor',
  '/territorios': 'listaTerritorio',
  '/informes': 'estadisticas',
  '/rutas': 'rutas',
  '/asignados': 'tAsignados',
  '/territorio': 'formTerritorio',
  '/territorios-nuevos': 'crearTerritorios',
  '/tarjeta': 'tarjetaTerritorio',
  '/configuracion': 'parametros',
  '/usuarios': 'userManagement',
  '/admin': 'adminPanel',
  '/grupos-invitados': 'gruposInvitados',
  '/plantillas-mensaje': 'plantillasMensaje',
  '/metadata-enlaces': 'metadataEnlaces',
  '/alojamientos': 'alojamientos',
  '/estudios': 'estudiosTerritorio',
  '/transporte': 'transporteTerritorio',
  '/map': 'map_viewer',
  '/informe': 'registroTrabajado',
  '/print': 'print',
  '/salida-publica': 'salidaPublica',
  '/salida-publica-mayores': 'salidaPublicaMayores',
  '/monitor': 'monitorSalida',
  '/enlaces': 'enlacesSalidas',
  '/enlaces-informes': 'enlacesInformes',
  '/informe-conductor': 'informeConductor',
  '/alojamiento-publico': 'alojamientoPublico',
  '/estudios-publico': 'estudiosPublico',
  '/estudio-asignacion': 'estudioAsignacionPublico',
  '/registro-congregacion': 'registroCongregacion',
  '/gestion-usuarios': 'gestionUsuarios',
  '/enlaces-estudios': 'estudiosDashboard',
  '/estudios-dashboard-publico': 'estudiosDashboardPublico',
  '/legal': 'legal',
}

// Pages that carry query params in the URL (?id=<uuid>)
const PARAMETERIZED_PAGES = new Set([
  'formTerritorio',
  'tarjetaTerritorio',
  'estudiosTerritorio',
  'transporteTerritorio',
  'salidaPublica',
  'salidaPublicaMayores',
  'informeConductor',
  'monitorSalida',
  'alojamientoPublico',
  'estudiosPublico',
  'estudioAsignacionPublico',
  'metadataEnlaces',
  'registroCongregacion',
  'gestionUsuarios',
  'estudiosDashboardPublico',
])

export const useRouterStore = defineStore('router', () => {
  const page = ref(PATH_TO_PAGE[document.location.pathname] || null)
  const params = ref(Object.fromEntries(new URLSearchParams(document.location.search)))

  // Snapshot of what was in the URL when the app first loaded (for post-login restore)
  const initialRoute = Object.freeze({
    page: page.value,
    params: { ...params.value },
  })

  // El bypass de login para e2e (?e2e=1) debe sobrevivir a la navegación interna:
  // una vez presente en la URL inicial, se reinyecta en cada pushState para no
  // perder la sesión de pruebas al cambiar de página.
  const e2eFlag = new URLSearchParams(document.location.search).get('e2e')

  // Update URL when page or params change
  watch(
    [page, params],
    ([newPage, newParams]) => {
      if (!newPage) return
      const path = PAGE_TO_PATH[newPage]
      if (!path) return
      const qs = new URLSearchParams(PARAMETERIZED_PAGES.has(newPage) ? newParams : {})
      if (e2eFlag) qs.set('e2e', e2eFlag)
      const qsString = qs.toString()
      const full = qsString ? `${path}?${qsString}` : path
      if (window.location.pathname + window.location.search !== full) {
        history.pushState({}, '', full)
      }
    },
    { deep: true }
  )

  // Sync page and params from URL on browser back/forward
  window.addEventListener('popstate', () => {
    page.value = PATH_TO_PAGE[window.location.pathname] || null
    params.value = Object.fromEntries(new URLSearchParams(window.location.search))
  })

  return { page, params, initialRoute }
})
